import { useEffect, useRef } from 'react';

const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, label, [role="button"], [data-cursor="hover"]';

const CustomCursor = () => {
  const ringRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia('(hover: none)').matches) return;

    const cursorRing = ringRef.current;
    if (!cursorRing) return;

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let ringX = mouseX, ringY = mouseY;
    let isFirstMove = true;
    let scale = 0;
    let targetScale = 0;
    let isHoveringInteractive = false;
    let insideWindow = true;
    let rafId;

    const refreshHoverState = () => {
      const el = document.elementFromPoint(mouseX, mouseY);
      const nowInteractive = !!(el && el.closest && el.closest(INTERACTIVE_SELECTOR));
      if (nowInteractive !== isHoveringInteractive) {
        isHoveringInteractive = nowInteractive;
        if (nowInteractive) cursorRing.classList.add('expanded');
        else cursorRing.classList.remove('expanded');
      }
      if (insideWindow) targetScale = 1; // ring is always visible when the cursor is on screen
    };

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (isFirstMove) {
        ringX = mouseX; ringY = mouseY;
        isFirstMove = false;
        cursorRing.classList.add('active');
      }
      insideWindow = true;
      refreshHoverState();
    };
    const onLeave = () => { insideWindow = false; targetScale = 0; };
    const onEnter = () => { insideWindow = true; refreshHoverState(); };
    const onClick = () => {
      requestAnimationFrame(() => requestAnimationFrame(refreshHoverState));
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('mouseenter', onEnter);
    document.addEventListener('click', onClick, true);
    window.addEventListener('scroll', refreshHoverState, { passive: true });
    window.addEventListener('resize', refreshHoverState);

    const loop = () => {
      ringX = mouseX;
      ringY = mouseY;
      scale += (targetScale - scale) * 0.15;
      const currentRingScale = cursorRing.classList.contains('expanded') ? 1.6 * scale : scale;
      cursorRing.style.transform = `translate3d(${ringX}px, ${ringY}px, 0) translate(-50%, -50%) scale(${currentRingScale})`;
      rafId = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('mouseenter', onEnter);
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('scroll', refreshHoverState);
      window.removeEventListener('resize', refreshHoverState);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return <div id="cursor-ring" ref={ringRef} className="cursor-ring-outline" />;
};

export default CustomCursor;
